import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  QrCode, Download, Shield, FileText, Link as LinkIcon, Check, Building,
  Clock, Plus, Trash2, Calendar, Save, AlertCircle, Sparkles, Briefcase,
  MessageSquare, Send, Utensils, MapPin, Star, UsersRound, Link2,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import PrivacyPolicyDiffModal from '../components/PrivacyPolicyDiffModal';

import { getBackendUrl } from '../../services/origin';
const API = getBackendUrl();

const DAYS = [
  { key: 'mon', label: 'Lun' },
  { key: 'tue', label: 'Mar' },
  { key: 'wed', label: 'Mer' },
  { key: 'thu', label: 'Gio' },
  { key: 'fri', label: 'Ven' },
  { key: 'sat', label: 'Sab' },
  { key: 'sun', label: 'Dom' },
];

const DEFAULT_DAY = { closed: false, slots: [{ open: '12:00', close: '15:00' }, { open: '19:00', close: '23:00' }] };

// Read first slot (with legacy single-slot fallback) to seed UI when needed
const getSlots = (day) => {
  if (!day) return [{ open: '12:00', close: '23:00' }];
  if (Array.isArray(day.slots) && day.slots.length > 0) return day.slots;
  return [{ open: day.open || '12:00', close: day.close || '23:00' }];
};

export default function AdminLocale() {
  const [form, setForm] = useState({ privacy_policy_url: '', privacy_policy_file_url: '', registration_hero_text: '' });
  const [businessName, setBusinessName] = useState('');
  const [qrData, setQrData] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPrivacy, setUploadingPrivacy] = useState(false);
  const [generatingPrivacy, setGeneratingPrivacy] = useState(false);
  const [privacyVersions, setPrivacyVersions] = useState([]);
  const [diffVersion, setDiffVersion] = useState(null); // { id, label } or null

  // Company data state
  const [companyData, setCompanyData] = useState({
    company_name: '', vat_number: '', legal_address: '', company_phone: '', company_email: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // SMS Template state
  const [smsTemplate, setSmsTemplate] = useState({ program_name: 'programma fedelta', custom_text: '' });
  const [savingSms, setSavingSms] = useState(false);
  const [testingSms, setTestingSms] = useState(false);

  // Venue Profile state
  const [venueProfile, setVenueProfile] = useState({
    venue_type: '', description: '', specialties: '', target_audience: '',
    cuisine_type: '', ambiance: '', capacity: '',
    important_dates: [],
  });
  const [savingVenue, setSavingVenue] = useState(false);
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState({ date: '', label: '' });

  // Hours state
  const [standardHours, setStandardHours] = useState(null);
  const [specialHours, setSpecialHours] = useState([]);
  const [formattedHours, setFormattedHours] = useState('');
  const [savingHours, setSavingHours] = useState(false);
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [newSpecial, setNewSpecial] = useState({ date: '', label: '', open: '09:00', close: '23:00', closed: false });
  const [publicUrl, setPublicUrl] = useState('');
  const [savingPublicUrl, setSavingPublicUrl] = useState(false);

  useEffect(() => {
    Promise.all([
      adminApi.get('/business/branding'),
      adminApi.get('/business/registration-qr'),
      adminApi.get('/business/hours'),
      adminApi.get('/business/company-data'),
      adminApi.get('/business/sms-template'),
      adminApi.get('/business/venue-profile'),
      adminApi.get('/business/public-url').catch(() => ({ data: { public_url: '' } })),
    ]).then(([brandRes, qrRes, hoursRes, companyRes, smsRes, venueRes, pubUrlRes]) => {
      const r = brandRes.data;
      setForm({ privacy_policy_url: r.privacy_policy_url || '', privacy_policy_file_url: r.privacy_policy_file_url || '', registration_hero_text: r.registration_hero_text || '' });
      setBusinessName(r.business_name || '');
      setQrData(qrRes.data.qr_data);
      setQrUrl(qrRes.data.url);
      setStandardHours(hoursRes.data.standard_hours);
      setSpecialHours(hoursRes.data.special_hours || []);
      setFormattedHours(hoursRes.data.formatted || '');
      const cd = companyRes.data || {};
      setCompanyData({
        company_name: cd.company_name || '', vat_number: cd.vat_number || '',
        legal_address: cd.legal_address || '', company_phone: cd.company_phone || '',
        company_email: cd.company_email || '',
      });
      const sms = smsRes.data || {};
      setSmsTemplate({
        program_name: sms.program_name || 'programma fedelta',
        custom_text: sms.custom_text || '',
      });
      const vp = venueRes.data || {};
      setVenueProfile({
        venue_type: vp.venue_type || '', description: vp.description || '',
        specialties: vp.specialties || '', target_audience: vp.target_audience || '',
        cuisine_type: vp.cuisine_type || '', ambiance: vp.ambiance || '',
        capacity: vp.capacity || '',
        important_dates: vp.important_dates || [],
      });
      setPublicUrl(pubUrlRes?.data?.public_url || '');
    }).catch(() => {}).finally(() => setLoading(false));
    // Load privacy versions independently — non-blocking for the page.
    adminApi.get('/admin/privacy-versions')
      .then(r => setPrivacyVersions(r.data?.versions || []))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put('/business/branding', {
        business_name: businessName || null,
        privacy_policy_url: form.privacy_policy_url || null,
        privacy_policy_file_url: form.privacy_policy_file_url || null,
        registration_hero_text: form.registration_hero_text || null,
      });
      toast.success('Impostazioni locale salvate!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const handlePrivacyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    setUploadingPrivacy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.post('/business/upload-privacy', fd, { headers: { 'Content-Type': 'multipart/form-data' }});
      setForm(f => ({ ...f, privacy_policy_file_url: res.data.url }));
      toast.success('File caricato!');
    } catch { toast.error('Errore nel caricamento'); }
    finally { setUploadingPrivacy(false); }
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      await adminApi.put('/business/company-data', companyData);
      toast.success('Dati aziendali salvati!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingCompany(false); }
  };

  const handleSaveSms = async () => {
    setSavingSms(true);
    try {
      await adminApi.put('/business/sms-template', smsTemplate);
      toast.success('Template SMS salvato!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingSms(false); }
  };

  const handleSaveVenueProfile = async () => {
    setSavingVenue(true);
    try {
      await adminApi.put('/business/venue-profile', {
        ...venueProfile,
        capacity: venueProfile.capacity ? parseInt(venueProfile.capacity) : null,
      });
      toast.success('Profilo locale salvato!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingVenue(false); }
  };

  const addImportantDate = () => {
    if (!newDate.date || !newDate.label) { toast.error('Compila data e descrizione'); return; }
    const mmdd = newDate.date.slice(5); // from YYYY-MM-DD to MM-DD
    setVenueProfile(vp => ({
      ...vp,
      important_dates: [...vp.important_dates, { date: mmdd, label: newDate.label, recurring: true }],
    }));
    setNewDate({ date: '', label: '' });
    setShowAddDate(false);
  };

  const removeImportantDate = (idx) => {
    setVenueProfile(vp => ({
      ...vp,
      important_dates: vp.important_dates.filter((_, i) => i !== idx),
    }));
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const res = await adminApi.post('/business/sms-template/test');
      toast.success(`SMS di test inviato a ${res.data.phone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio SMS di test');
    }
    finally { setTestingSms(false); }
  };

  const handleGeneratePrivacy = async () => {
    setGeneratingPrivacy(true);
    try {
      const res = await adminApi.post('/business/generate-privacy-policy');
      const url = res.data.privacy_policy_file_url;
      setForm(f => ({ ...f, privacy_policy_file_url: url }));
      // Also save to branding
      await adminApi.put('/business/branding', {
        business_name: businessName || null,
        privacy_policy_url: form.privacy_policy_url || null,
        privacy_policy_file_url: url,
        registration_hero_text: form.registration_hero_text || null,
      });
      toast.success(res.data?.message || 'Privacy policy generata e salvata!');
      loadPrivacyVersions();
    } catch (e) {
      // Backend returns a clear 400 if company_data is incomplete.
      // Surface it verbatim so the operator knows exactly what to fill.
      const msg = e.response?.data?.detail || 'Errore nella generazione';
      toast.error(msg);
    }
    finally { setGeneratingPrivacy(false); }
  };

  const loadPrivacyVersions = async () => {
    try {
      const r = await adminApi.get('/admin/privacy-versions');
      setPrivacyVersions(r.data?.versions || []);
    } catch { /* non-fatal */ }
  };

  const updateDay = (day, field, value) => {
    setStandardHours(h => ({ ...h, [day]: { ...(h?.[day] || DEFAULT_DAY), [field]: value } }));
  };

  const updateSlot = (day, idx, field, value) => {
    setStandardHours(h => {
      const cur = h?.[day] || DEFAULT_DAY;
      const slots = getSlots(cur).map((s, i) => i === idx ? { ...s, [field]: value } : s);
      return { ...h, [day]: { ...cur, slots, open: undefined, close: undefined } };
    });
  };

  const addSlot = (day) => {
    setStandardHours(h => {
      const cur = h?.[day] || DEFAULT_DAY;
      const existing = getSlots(cur);
      const last = existing[existing.length - 1];
      // Suggest next slot 1h after last close, default 1h duration
      const nextOpen = last?.close || '19:00';
      return { ...h, [day]: { ...cur, slots: [...existing, { open: nextOpen, close: '23:00' }], open: undefined, close: undefined } };
    });
  };

  const removeSlot = (day, idx) => {
    setStandardHours(h => {
      const cur = h?.[day] || DEFAULT_DAY;
      const existing = getSlots(cur).filter((_, i) => i !== idx);
      const next = existing.length > 0 ? existing : [{ open: '12:00', close: '23:00' }];
      return { ...h, [day]: { ...cur, slots: next, open: undefined, close: undefined } };
    });
  };

  const handleSaveHours = async () => {
    setSavingHours(true);
    try {
      const res = await adminApi.put('/business/hours', { standard_hours: standardHours });
      setFormattedHours(res.data.formatted);
      toast.success('Orari standard salvati!');
    } catch { toast.error('Errore nel salvataggio orari'); }
    finally { setSavingHours(false); }
  };

  const handleAddSpecial = async () => {
    if (!newSpecial.date) { toast.error('Seleziona una data'); return; }
    try {
      const res = await adminApi.post('/business/hours/special', newSpecial);
      setSpecialHours(res.data);
      setNewSpecial({ date: '', label: '', open: '09:00', close: '23:00', closed: false });
      setShowAddSpecial(false);
      toast.success('Orario speciale aggiunto!');
    } catch { toast.error('Errore'); }
  };

  const handleDeleteSpecial = async (id) => {
    try {
      const res = await adminApi.delete(`/business/hours/special/${id}`);
      setSpecialHours(res.data);
      toast.success('Rimosso');
    } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="admin-locale-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Locale</h1>
        <p className="text-[#64748B] text-sm">Orari, QR code, privacy policy e dati del locale</p>
      </div>

      <div className="space-y-6">
        {/* Business Name */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-name-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><Building size={16} /> Nome del locale</h3>
          <p className="text-[#64748B] text-xs mb-3">Il nome del locale viene usato nei messaggi WhatsApp, nella bacheca clienti e nella pagina di registrazione.</p>
          <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)}
            data-testid="business-name-input" placeholder="Es. Caffè Roma" maxLength={80}
            className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
        </div>

        {/* Public URL (production domain) */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-public-url-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1 flex items-center gap-2">
            <Link2 size={16} className="text-[#D44E20]" /> Dominio pubblico
          </h3>
          <p className="text-[#64748B] text-xs mb-3">
            Quando imposti questo URL, <strong>tutti i link nelle email/SMS di conferma biglietti</strong> (e in generale
            tutte le comunicazioni transazionali) puntano sempre a questo dominio,
            indipendentemente da quale ambiente ha generato l'email.
            <br /><br />
            Esempio: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">https://ring.tacoloco.food</code>.
            Lascia vuoto per usare automaticamente il dominio dal quale il cliente sta navigando.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={publicUrl}
              onChange={(e) => setPublicUrl(e.target.value)}
              placeholder="https://ring.tacoloco.food"
              data-testid="public-url-input"
              className="flex-1 h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none font-mono"
            />
            <button
              type="button"
              disabled={savingPublicUrl}
              onClick={async () => {
                setSavingPublicUrl(true);
                try {
                  const r = await adminApi.put('/business/public-url', { public_url: publicUrl.trim() });
                  setPublicUrl(r.data?.public_url || '');
                  toast.success(r.data?.public_url ? 'Dominio pubblico aggiornato' : 'Dominio pubblico cancellato');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Errore di salvataggio');
                } finally {
                  setSavingPublicUrl(false);
                }
              }}
              data-testid="public-url-save-btn"
              className="h-10 px-4 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#333] disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {savingPublicUrl ? 'Salvataggio…' : 'Salva'}
            </button>
          </div>
        </div>

        {/* Venue Profile */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="venue-profile-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1 flex items-center gap-2"><Utensils size={16} className="text-[#D44E20]" /> Profilo del locale</h3>
          <p className="text-[#64748B] text-xs mb-4">Queste informazioni vengono usate dall'AI per generare suggerimenti di marketing piu mirati e stagionali.</p>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Tipo di locale</label>
                <select value={venueProfile.venue_type} onChange={e => setVenueProfile(v => ({ ...v, venue_type: e.target.value }))}
                  data-testid="venue-type-select"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none appearance-none">
                  <option value="">Seleziona...</option>
                  <option value="ristorante">Ristorante</option>
                  <option value="ristorante_messicano">Ristorante Messicano</option>
                  <option value="bar">Bar</option>
                  <option value="pub">Pub</option>
                  <option value="pizzeria">Pizzeria</option>
                  <option value="trattoria">Trattoria</option>
                  <option value="cocktail_bar">Cocktail Bar</option>
                  <option value="wine_bar">Wine Bar</option>
                  <option value="gelateria">Gelateria</option>
                  <option value="pasticceria">Pasticceria</option>
                  <option value="fast_food">Fast Food</option>
                  <option value="food_truck">Food Truck</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Tipo di cucina</label>
                <input type="text" value={venueProfile.cuisine_type}
                  onChange={e => setVenueProfile(v => ({ ...v, cuisine_type: e.target.value }))}
                  data-testid="cuisine-type-input" placeholder="Es. Messicana, Italiana, Fusion..."
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Descrizione del locale</label>
              <textarea value={venueProfile.description}
                onChange={e => setVenueProfile(v => ({ ...v, description: e.target.value }))}
                data-testid="venue-description-input" placeholder="Racconta il tuo locale: storia, atmosfera, cosa lo rende speciale..."
                rows={3} maxLength={500}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none" />
              <p className="text-right text-[10px] text-[#999] mt-0.5">{venueProfile.description.length}/500</p>
            </div>

            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Specialita / Piatti forti</label>
              <input type="text" value={venueProfile.specialties}
                onChange={e => setVenueProfile(v => ({ ...v, specialties: e.target.value }))}
                data-testid="venue-specialties-input" placeholder="Es. Tacos al pastor, Margarita classica, Nachos artigianali..."
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Pubblico target</label>
                <input type="text" value={venueProfile.target_audience}
                  onChange={e => setVenueProfile(v => ({ ...v, target_audience: e.target.value }))}
                  data-testid="venue-audience-input" placeholder="Es. Giovani 25-35, famiglie..."
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Atmosfera</label>
                <input type="text" value={venueProfile.ambiance}
                  onChange={e => setVenueProfile(v => ({ ...v, ambiance: e.target.value }))}
                  data-testid="venue-ambiance-input" placeholder="Es. Informale, elegante, festosa..."
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Capienza (posti a sedere)</label>
              <input type="number" value={venueProfile.capacity}
                onChange={e => setVenueProfile(v => ({ ...v, capacity: e.target.value }))}
                data-testid="venue-capacity-input" placeholder="Es. 50"
                className="w-32 h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>

            {/* Important Dates */}
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] text-[#64748B] font-medium flex items-center gap-1"><Calendar size={10} /> Date importanti ricorrenti</label>
                <button onClick={() => setShowAddDate(!showAddDate)} data-testid="add-date-btn"
                  className="flex items-center gap-1 px-2 py-1 bg-[#D44E20]/10 text-[#D44E20] text-[10px] font-medium rounded-lg hover:bg-[#D44E20]/20 transition-colors">
                  <Plus size={10} /> Aggiungi
                </button>
              </div>
              <p className="text-[#aaa] text-[10px] mb-2">Anniversari, festività tematiche, eventi ricorrenti. L'AI li userà per suggerire promozioni a tema.</p>

              {showAddDate && (
                <div className="flex items-end gap-2 mb-3 p-3 bg-slate-50 rounded-xl" data-testid="add-date-form">
                  <div className="flex-1">
                    <label className="text-[10px] text-[#64748B] font-medium block mb-1">Data (giorno/mese)</label>
                    <input type="date" value={newDate.date} onChange={e => setNewDate(d => ({ ...d, date: e.target.value }))}
                      data-testid="new-date-input"
                      className="w-full h-9 bg-white border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-[#64748B] font-medium block mb-1">Descrizione</label>
                    <input type="text" value={newDate.label} onChange={e => setNewDate(d => ({ ...d, label: e.target.value }))}
                      placeholder="Es. Cinco de Mayo" data-testid="new-date-label"
                      className="w-full h-9 bg-white border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                  <button onClick={addImportantDate} data-testid="confirm-add-date"
                    className="h-9 px-3 bg-[#D44E20] text-white text-xs font-bold rounded-lg hover:bg-[#B83D15]">Aggiungi</button>
                </div>
              )}

              {venueProfile.important_dates.length === 0 ? (
                <p className="text-[#ccc] text-xs text-center py-3">Nessuna data importante configurata</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {venueProfile.important_dates.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-[#FFF3E0] border border-[#FFE0B2] rounded-lg px-2.5 py-1.5" data-testid={`important-date-${i}`}>
                      <Star size={10} className="text-[#F59E0B]" />
                      <span className="text-xs font-medium text-[#E65100]">{d.label}</span>
                      <span className="text-[10px] text-[#F57C00]">({d.date})</span>
                      <button onClick={() => removeImportantDate(i)} className="ml-1 text-[#FF8A65] hover:text-red-500 transition-colors"><Trash2 size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={handleSaveVenueProfile} disabled={savingVenue} data-testid="save-venue-profile-btn"
              className="w-full h-10 bg-[#D44E20] text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
              <Save size={14} /> {savingVenue ? 'Salvataggio...' : 'Salva profilo locale'}
            </button>
          </div>
        </div>

        {/* SMS Template */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-sms-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1 flex items-center gap-2"><MessageSquare size={16} className="text-[#D44E20]" /> Messaggio SMS con PIN</h3>
          <p className="text-[#64748B] text-xs mb-4">Personalizza l'SMS che viene inviato ai nuovi clienti con il PIN di accesso alla bacheca.</p>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Nome del programma fedelta</label>
              <input type="text" value={smsTemplate.program_name}
                onChange={e => setSmsTemplate(t => ({ ...t, program_name: e.target.value }))}
                data-testid="sms-program-name" placeholder="Es. Club VIP, Fidelity Card..."
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Testo personalizzato (opzionale)</label>
              <textarea value={smsTemplate.custom_text}
                onChange={e => { if (e.target.value.length <= 200) setSmsTemplate(t => ({ ...t, custom_text: e.target.value })); }}
                data-testid="sms-custom-text" placeholder="Es. Salva questo numero per restare aggiornato!"
                rows={2}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none" />
              <div className="flex justify-end mt-0.5">
                <span className={`text-[10px] ${smsTemplate.custom_text.length > 160 ? 'text-amber-500' : 'text-[#999]'}`}>{smsTemplate.custom_text.length}/200</span>
              </div>
            </div>

            {/* Live preview */}
            <div className="bg-gray-50 rounded-xl p-4 border border-slate-200">
              <p className="text-[10px] text-[#64748B] font-medium mb-2">Anteprima SMS</p>
              <div className="bg-white rounded-lg p-3 text-xs text-[#333] leading-relaxed border border-slate-100 shadow-inner whitespace-pre-wrap">
                {`Ciao! Benvenuto nel ${smsTemplate.program_name || 'programma fedelta'} di ${businessName || 'Nome Locale'}.\n\nIl tuo PIN personale e': 847291\nAccedi alla tua bacheca: https://app.lazo.it/my/abc123${smsTemplate.custom_text ? '\n\n' + smsTemplate.custom_text : ''}`}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={handleSaveSms} disabled={savingSms} data-testid="save-sms-btn"
                className="flex-1 h-9 bg-[#111114] text-white text-xs font-bold rounded-xl hover:bg-[#333] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                <Save size={12} />{savingSms ? 'Salvataggio...' : 'Salva template'}
              </button>
              <button onClick={handleTestSms} disabled={testingSms} data-testid="test-sms-btn"
                className="h-9 px-4 bg-[#D44E20] text-white text-xs font-bold rounded-xl hover:opacity-90 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                <Send size={12} />{testingSms ? 'Invio...' : 'Invia SMS di test'}
              </button>
            </div>
          </div>
        </div>

        {/* ─── Standard Hours ───────────────────────────────────── */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="standard-hours-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1 flex items-center gap-2"><Clock size={16} className="text-[#D44E20]" /> Orari di apertura standard</h3>
          <p className="text-[#64748B] text-xs mb-4">Configura gli orari settimanali del locale. Questi orari verranno usati nel messaggio broadcast di aggiornamento orari.</p>

          <div className="space-y-2">
            {DAYS.map(({ key, label }) => {
              const day = standardHours?.[key] || DEFAULT_DAY;
              const slots = getSlots(day);
              return (
                <div key={key} className={`flex flex-col gap-2 p-2.5 rounded-xl border transition-colors ${day.closed ? 'bg-gray-50 border-slate-100' : 'bg-white border-slate-200'}`} data-testid={`day-${key}`}>
                  <div className="flex items-center gap-2 md:gap-3">
                    <PageTooltip page="locale" />
                    <span className="w-8 text-xs font-bold text-[#1A1A1A]">{label}</span>
                    <button
                      onClick={() => updateDay(key, 'closed', !day.closed)}
                      data-testid={`toggle-${key}`}
                      className={`w-10 h-5 rounded-full relative transition-all flex-shrink-0 ${day.closed ? 'bg-gray-300' : 'bg-[#3ECF8E]'}`}
                    >
                      <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] transition-all shadow-sm ${day.closed ? 'left-[3px]' : 'left-[23px]'}`} />
                    </button>
                    {day.closed ? (
                      <span className="text-xs text-[#999] italic">Chiuso</span>
                    ) : (
                      <div className="flex-1 flex flex-col gap-1.5">
                        {slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-1.5" data-testid={`slot-${key}-${idx}`}>
                            <input type="time" value={slot.open} onChange={e => updateSlot(key, idx, 'open', e.target.value)}
                              data-testid={`open-${key}-${idx}`}
                              className="h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                            <span className="text-[10px] text-[#aaa]">-</span>
                            <input type="time" value={slot.close} onChange={e => updateSlot(key, idx, 'close', e.target.value)}
                              data-testid={`close-${key}-${idx}`}
                              className="h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                            {slots.length > 1 && (
                              <button onClick={() => removeSlot(key, idx)} data-testid={`remove-slot-${key}-${idx}`}
                                title="Rimuovi fascia"
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-[#D44E20] hover:bg-[#D44E20]/10 transition-colors flex-shrink-0">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!day.closed && (
                    <button onClick={() => addSlot(key)} data-testid={`add-slot-${key}`}
                      className="self-start ml-10 flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-[#D44E20] hover:bg-[#D44E20]/10 rounded-lg transition-colors">
                      <Plus size={11} /> Aggiungi fascia oraria
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Preview formatted */}
          {formattedHours && (
            <div className="mt-4 bg-[#E7F8EC] border border-[#C8E6C9] rounded-xl p-3" data-testid="hours-preview">
              <p className="text-[10px] text-[#4CAF50] font-medium mb-1">Anteprima (come appare nel messaggio WhatsApp)</p>
              <p className="text-[12px] text-[#1B5E20] whitespace-pre-line font-mono">{formattedHours}</p>
            </div>
          )}

          <button onClick={handleSaveHours} disabled={savingHours} data-testid="save-hours-btn"
            className="mt-3 w-full h-10 bg-[#D44E20] text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
            <Save size={14} /> {savingHours ? 'Salvataggio...' : 'Salva orari standard'}
          </button>
        </div>

        {/* ─── Special Hours ───────────────────────────────────── */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="special-hours-section">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2"><Calendar size={16} className="text-[#D44E20]" /> Aperture/chiusure straordinarie</h3>
            <button onClick={() => setShowAddSpecial(!showAddSpecial)} data-testid="add-special-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#D44E20]/10 text-[#D44E20] text-xs font-medium rounded-lg hover:bg-[#D44E20]/20 transition-colors">
              <Plus size={12} /> Aggiungi
            </button>
          </div>
          <p className="text-[#64748B] text-xs mb-4">Gestisci chiusure per feste, aperture straordinarie per eventi o orari modificati. Non vengono inviati nel broadcast orari standard.</p>

          {/* Add form */}
          {showAddSpecial && (
            <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="add-special-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#64748B] font-medium block mb-1">Data</label>
                  <input type="date" value={newSpecial.date} onChange={e => setNewSpecial(s => ({ ...s, date: e.target.value }))}
                    data-testid="special-date-input"
                    className="w-full h-9 bg-white border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#64748B] font-medium block mb-1">Descrizione</label>
                  <input type="text" value={newSpecial.label} onChange={e => setNewSpecial(s => ({ ...s, label: e.target.value }))}
                    placeholder="Es. Festa, Evento..." data-testid="special-label-input"
                    className="w-full h-9 bg-white border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setNewSpecial(s => ({ ...s, closed: !s.closed }))} data-testid="special-closed-toggle"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    newSpecial.closed ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white border-slate-200 text-[#64748B]'
                  }`}>
                  {newSpecial.closed ? 'Chiuso tutto il giorno' : 'Aperto con orari speciali'}
                </button>
                {!newSpecial.closed && (
                  <div className="flex items-center gap-1.5">
                    <input type="time" value={newSpecial.open} onChange={e => setNewSpecial(s => ({ ...s, open: e.target.value }))}
                      className="h-8 bg-white border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                    <span className="text-[10px] text-[#aaa]">-</span>
                    <input type="time" value={newSpecial.close} onChange={e => setNewSpecial(s => ({ ...s, close: e.target.value }))}
                      className="h-8 bg-white border border-slate-200 rounded-lg px-2 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddSpecial} data-testid="confirm-add-special"
                  className="flex-1 h-9 bg-[#D44E20] text-white text-xs font-bold rounded-lg hover:bg-[#B83D15] transition-colors">Salva</button>
                <button onClick={() => setShowAddSpecial(false)}
                  className="h-9 px-4 bg-slate-100 text-[#64748B] text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors">Annulla</button>
              </div>
            </div>
          )}

          {/* Special hours list */}
          {specialHours.length === 0 && !showAddSpecial ? (
            <div className="text-center py-6 text-[#999]">
              <Calendar size={24} className="mx-auto mb-2 text-[#ddd]" />
              <p className="text-xs">Nessun orario speciale configurato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {specialHours.map(sh => (
                <div key={sh.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`special-${sh.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#1A1A1A]">
                        {sh.date ? new Date(sh.date + 'T00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </span>
                      {sh.label && <span className="text-[10px] text-[#64748B] bg-white px-2 py-0.5 rounded-md border border-slate-100">{sh.label}</span>}
                    </div>
                    <p className={`text-[11px] mt-0.5 ${sh.closed ? 'text-red-500 font-medium' : 'text-[#64748B]'}`}>
                      {sh.closed ? 'Chiuso' : `${sh.open || '?'} - ${sh.close || '?'}`}
                    </p>
                  </div>
                  <button onClick={() => handleDeleteSpecial(sh.id)} data-testid={`delete-special-${sh.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#bbb] hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
            <AlertCircle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-700">Gli orari speciali sono solo per la tua gestione interna. Il broadcast "Aggiornamento Orari" invia solo gli orari standard.</p>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-qr-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><QrCode size={16} /> QR Code Registrazione</h3>
          <p className="text-[#64748B] text-xs mb-4">Stampa questo QR code e posizionalo nel tuo locale. I clienti potranno registrarsi autonomamente scansionandolo.</p>
          <div className="mb-4">
            <label className="text-[10px] text-[#64748B] font-medium block mb-1">Messaggio nella pagina di registrazione</label>
            <input type="text" value={form.registration_hero_text}
              onChange={e => setForm(f => ({ ...f, registration_hero_text: e.target.value }))}
              data-testid="registration-hero-input" placeholder={`Es. Unisciti al programma fedeltà di ${businessName}!`} maxLength={120}
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            <p className="text-[#64748B] text-[10px] mt-1">{form.registration_hero_text.length}/120</p>
          </div>
          {qrData && (
            <div className="flex items-start gap-4 bg-slate-50 rounded-xl p-4">
              <img src={qrData} alt="QR Code" className="w-32 h-32 rounded-xl border border-slate-200 flex-shrink-0" data-testid="registration-qr-img" />
              <div className="flex-1 space-y-2">
                <p className="text-[#1A1A1A] text-xs break-all font-mono bg-white p-2 rounded-lg border border-slate-200">{qrUrl}</p>
                <a href={qrData} download={`qr-registrazione-${businessName.replace(/\s/g, '-').toLowerCase()}.png`}
                  data-testid="download-qr-btn"
                  className="flex items-center justify-center gap-1.5 w-full h-9 bg-[#D44E20] text-white text-xs font-semibold rounded-xl hover:bg-[#B83D15] transition-colors">
                  <Download size={14} /> Scarica QR Code
                </a>
                <button onClick={() => { navigator.clipboard.writeText(qrUrl); toast.success('Link copiato!'); }}
                  className="w-full h-9 bg-white text-[#64748B] text-xs font-medium rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                  Copia link registrazione
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Dati Aziendali */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-company-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><Briefcase size={16} /> Dati Aziendali</h3>
          <p className="text-[#64748B] text-xs mb-4">Utilizzati per la generazione automatica della privacy policy e per identificare il titolare del trattamento dati.</p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Ragione Sociale</label>
              <input type="text" value={companyData.company_name}
                onChange={e => setCompanyData(d => ({ ...d, company_name: e.target.value }))}
                data-testid="company-name-input" placeholder="Es. Taco Loco S.r.l."
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Partita IVA</label>
                <input type="text" value={companyData.vat_number}
                  onChange={e => setCompanyData(d => ({ ...d, vat_number: e.target.value }))}
                  data-testid="company-vat-input" placeholder="IT12345678901"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B] font-medium block mb-1">Telefono Aziendale</label>
                <input type="tel" value={companyData.company_phone}
                  onChange={e => setCompanyData(d => ({ ...d, company_phone: e.target.value }))}
                  data-testid="company-phone-input" placeholder="+39 06 1234567"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Sede Legale</label>
              <input type="text" value={companyData.legal_address}
                onChange={e => setCompanyData(d => ({ ...d, legal_address: e.target.value }))}
                data-testid="company-address-input" placeholder="Via Roma 42, 00100 Roma (RM)"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] font-medium block mb-1">Email Aziendale</label>
              <input type="email" value={companyData.company_email}
                onChange={e => setCompanyData(d => ({ ...d, company_email: e.target.value }))}
                data-testid="company-email-input" placeholder="info@tacoloco.it"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <button onClick={handleSaveCompany} disabled={savingCompany} data-testid="save-company-btn"
              className="w-full h-9 bg-[#111114] text-white text-xs font-bold rounded-xl hover:bg-[#333] disabled:opacity-50 transition-colors">
              {savingCompany ? 'Salvataggio...' : 'Salva dati aziendali'}
            </button>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="locale-privacy-section">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2"><Shield size={16} /> Privacy Policy</h3>
          <p className="text-[#64748B] text-xs mb-3">Genera automaticamente la privacy policy GDPR oppure caricane una personalizzata.</p>

          {/* Auto-generate button */}
          <button onClick={handleGeneratePrivacy} disabled={generatingPrivacy} data-testid="generate-privacy-btn"
            className="w-full h-10 bg-gradient-to-r from-[#D44E20] to-[#E8652E] text-white text-xs font-bold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2 mb-4">
            <Sparkles size={14} />
            {generatingPrivacy ? 'Generazione in corso...' : 'Genera Privacy Policy automatica'}
          </button>
          {!companyData.company_name && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl mb-4">
              <AlertCircle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-amber-700">Compila prima i dati aziendali per una privacy policy completa con il titolare del trattamento corretto.</p>
            </div>
          )}

          {/* Version history — GDPR audit trail */}
          {privacyVersions.length > 0 && (
            <div className="rounded-xl border border-slate-100 bg-slate-50/40 mb-4" data-testid="privacy-versions-history">
              <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5">
                <Clock size={11} className="text-[#64748B]" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Storico versioni · {privacyVersions.length}</p>
              </div>
              <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {privacyVersions.map((v, idx) => (
                  <li key={v.id} className="px-3 py-2 flex items-center gap-2" data-testid={`privacy-version-${v.version}`}>
                    <span className="text-[11px] font-bold tabular-nums text-[#D44E20] flex-shrink-0">v{v.version}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-[#1A1A1A] truncate">
                        {v.company_snapshot?.company_name || '—'}
                        <span className="text-[#94A3B8]"> · P.IVA {v.company_snapshot?.vat_number || '—'}</span>
                      </p>
                      <p className="text-[10px] text-[#94A3B8]">
                        {v.created_at ? new Date(v.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        {v.generated_by_name && <> · {v.generated_by_name}</>}
                        {v.accepted_count > 0 && <> · <span className="text-emerald-600 font-semibold">{v.accepted_count} accettazioni</span></>}
                      </p>
                    </div>
                    {/* Diff button — hidden on the oldest version since there's nothing to compare with */}
                    {idx < privacyVersions.length - 1 && (
                      <button
                        onClick={() => setDiffVersion({ id: v.id, label: `v${v.version}` })}
                        data-testid={`privacy-diff-btn-${v.version}`}
                        className="text-[10px] text-[#D44E20] font-semibold hover:underline flex-shrink-0"
                        title="Confronta con la versione precedente"
                      >
                        Diff
                      </button>
                    )}
                    {v.file_url && (
                      <a href={v.file_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#D44E20] font-semibold hover:underline flex-shrink-0">PDF</a>
                    )}
                  </li>
                ))}
              </ul>
              <div className="px-3 py-1.5 border-t border-slate-100">
                <p className="text-[9px] text-[#94A3B8]">Ogni versione è una snapshot frozen: i clienti che l'hanno accettata restano vincolati a quel testo per audit GDPR.</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-[#64748B] text-[10px] mb-3">
            <div className="flex-1 h-px bg-gray-200" /><span>oppure carica manualmente</span><div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-[#64748B] font-medium mb-1 flex items-center gap-1"><LinkIcon size={10} /> Link URL</label>
              <input type="url" value={form.privacy_policy_url}
                onChange={e => setForm(f => ({ ...f, privacy_policy_url: e.target.value }))}
                data-testid="privacy-url-input" placeholder="https://www.tuosito.it/privacy-policy"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="flex items-center gap-2 text-[#64748B] text-[10px]">
              <div className="flex-1 h-px bg-gray-200" /><span>oppure</span><div className="flex-1 h-px bg-gray-200" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] font-medium mb-1 flex items-center gap-1"><FileText size={10} /> Carica file (PDF)</label>
              <div className="flex items-center gap-3">
                {form.privacy_policy_file_url ? (
                  <div className="flex-1 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                    <FileText size={14} className="text-green-600 flex-shrink-0" />
                    <span className="text-xs text-green-700 truncate flex-1">File caricato</span>
                    <button onClick={() => setForm(f => ({ ...f, privacy_policy_file_url: '' }))}
                      className="text-[#EF4444] text-xs font-medium hover:underline flex-shrink-0">Rimuovi</button>
                  </div>
                ) : (
                  <button onClick={() => document.getElementById('privacy-file-input')?.click()}
                    disabled={uploadingPrivacy} data-testid="upload-privacy-btn"
                    className="flex-1 h-9 bg-slate-100 hover:bg-gray-200 text-[#1A1A1A] text-xs font-medium rounded-xl transition-colors disabled:opacity-50">
                    {uploadingPrivacy ? 'Caricamento...' : 'Carica file PDF'}
                  </button>
                )}
                <input id="privacy-file-input" type="file" accept=".pdf,application/pdf" className="hidden" onChange={handlePrivacyUpload} />
              </div>
            </div>
            {(form.privacy_policy_url || form.privacy_policy_file_url) && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-2.5">
                <p className="text-blue-700 text-[10px] flex items-center gap-1"><Check size={10} /> La privacy policy verrà mostrata nella pagina di consenso</p>
              </div>
            )}
          </div>
        </div>

        {/* Save */}
        <button onClick={handleSave} disabled={saving} data-testid="save-locale-btn"
          className="w-full h-11 bg-[#D44E20] text-white text-sm font-bold rounded-xl hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </button>
      </div>
      {/* Privacy policy diff modal */}
      {diffVersion && (
        <PrivacyPolicyDiffModal
          versionId={diffVersion.id}
          versionLabel={diffVersion.label}
          onClose={() => setDiffVersion(null)}
        />
      )}
    </div>
  );
}
